import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { ConfirmPaymentData, ConfirmSetupData, StripeElements } from "@stripe/stripe-js";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@tellescope/ui";
import { FormEvent } from "react";
import { useTranslation } from "react-i18next";

export interface StripePaymentFormProps {
  businessName: string;
  paymentElementIsReady: boolean;
  chargeImmediately: boolean;
  onReady: () => void;
  onSuccess: () => void;
  cost: number;
  customPriceMessage?: string;
}

export const StripePaymentForm = ({
  businessName,
  paymentElementIsReady,
  chargeImmediately,
  onReady,
  onSuccess,
  cost,
  customPriceMessage,
}: StripePaymentFormProps) => {
  const { t } = useTranslation("forms");
  const price = (cost / 100).toFixed(2);
  const priceMessage =
    customPriceMessage ??
    (chargeImmediately
      ? t("You will be charged ${{price}} on form submission", { price })
      : t("You will be charged ${{price}}", { price }));
  const finalPriceMessage = priceMessage.replace(/\{\{PRICE\}\}/g, price);
  const stripe = useStripe();
  const elements = useElements();

  const confirmPaymentMutation = useMutation({
    mutationKey: ["stripe", "confirmPayment"],
    mutationFn: async (request: {
      elements: StripeElements;
      confirmParams?: Partial<ConfirmPaymentData>;
      redirect: "if_required";
    }) => {
      if (!stripe) throw new Error("Stripe not setup");

      const { error } = await stripe.confirmPayment(request);

      if (error) throw error;
    },
    onSuccess,
  });
  const confirmSetupMutation = useMutation({
    mutationKey: ["stripe", "confirmSetup"],
    mutationFn: async (request: {
      elements: StripeElements;
      confirmParams?: Partial<ConfirmSetupData>;
      redirect: "if_required";
    }) => {
      if (!stripe) throw new Error("Stripe not setup");

      const { error } = await stripe.confirmSetup(request);

      if (error) throw error;
    },
    onSuccess,
  });
  const error = chargeImmediately ? confirmPaymentMutation.error : confirmSetupMutation.error;

  const disabled = !stripe || !elements || !paymentElementIsReady;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (disabled) {
      return null;
    }

    if (chargeImmediately) {
      await confirmPaymentMutation.mutateAsync({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: window.location.href,
        },
      });
    } else {
      await confirmSetupMutation.mutateAsync({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });
    }
  };

  return (
    <form className="space-y-1" onSubmit={handleSubmit}>
      <PaymentElement
        onReady={onReady}
        options={{
          business: { name: businessName ?? "" },
        }}
      />
      <Button type="submit" disabled={disabled}>
        {chargeImmediately ? t("Make Payment") : t("Save Payment Details")}
      </Button>
      {cost > 0 && <p>{finalPriceMessage}</p>}
      {!!error && <p className="text-red-500">{error.message}</p>}
    </form>
  );
};
